import { TrackedEvents, trackEvent } from 'analytics';
import type { EvaluatedSpecification, SimpleEvaluatedSpecification } from 'api';
import { Trans, useTranslation } from 'i18n';
import { useEffect } from 'react';
import { Alert, AlertTitle, Box, Typography } from 'ui';
import {
  formatInstructionComparisonOperator,
  inverseComparisonOperator,
} from '../../utils/formatInstructionComparisonOperator/formatInstructionComparisonOperator';
import { formatOperandsList } from '../../utils/formatOperandsList/formatOperandsList';
import { formatThreshold } from '../../utils/formatThreshold/formatThreshold';

type EvaluatedSpecificationLike = EvaluatedSpecification | SimpleEvaluatedSpecification;

type Props = {
  specs: EvaluatedSpecificationLike[];
};

export const SpecificationAlerts = ({ specs }: Props) => {
  const { language, t } = useTranslation('features');
  const specsWithActionText = specs.filter(
    (spec) => spec.trade_company_id && spec.spec_triggered && spec.action_text && spec.action_text.trim() !== ''
  );

  useEffect(() => {
    if (specsWithActionText.length > 0) {
      trackEvent(TrackedEvents.QUALITY_CONTROL_SPECIFICATION_ALERT_TRIGGERED);
    }
  }, [specsWithActionText.length]);

  if (specsWithActionText.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning">
      <AlertTitle>{t('backlog.breakdowns.specificationAlerts.title')}</AlertTitle>
      <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
        {specsWithActionText.map((spec) => {
          const comparisonText = [
            spec.spec_comparison_operator
              ? formatInstructionComparisonOperator(inverseComparisonOperator(spec.spec_comparison_operator), 'long')
              : null,
            spec.spec_threshold_value != null ? formatThreshold(spec.spec_threshold_value) : null,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <li key={spec.id}>
              <Typography data-testid={`spec-alert-${spec.id}`}>
                <Trans
                  namespace="features"
                  i18nKey={
                    comparisonText
                      ? 'backlog.breakdowns.specificationAlerts.groupDescription'
                      : 'backlog.breakdowns.specificationAlerts.groupDescriptionWithoutComparison'
                  }
                  values={{
                    actionText: spec.action_text,
                    comparison: comparisonText,
                    name: spec.name || formatOperandsList(spec.operands, { language, t }),
                  }}
                  components={{
                    boldAction: (
                      <Typography
                        component="span"
                        fontWeight="bold"
                        sx={{
                          display: 'inline',
                        }}
                      />
                    ),
                    boldComparison: (
                      <Typography
                        component="span"
                        fontWeight="bold"
                        sx={{
                          display: 'inline',
                        }}
                      />
                    ),
                    boldName: (
                      <Typography
                        component="span"
                        fontWeight="bold"
                        sx={{
                          display: 'inline',
                        }}
                      />
                    ),
                  }}
                />
              </Typography>
            </li>
          );
        })}
      </Box>
    </Alert>
  );
};
